import Grid from '@mui/material/Grid2';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import { ChangeEvent, FC, useState } from 'react';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import { TCustomTheme } from '@mgm/common/theme';
import { useCustomTheme } from '@mgm/common/hooks/useCustomTheme';
import { Iconify } from '@mgm/common/components';
import { BaseLabel } from '@mgm/common/components/base';
import { useAppDispatch, useAppSelector } from '../../../../hooks/useAppStore';
import { getCurrencyByCode } from '../../../../store/common/selectors';
import { TCommissionPlan } from '../../../../types';
import { getSelectedCommissionPlan } from '../../../../store/commissions/selectors';
import { commissionsActions } from '../../../../store/commissions/slice';
import { BonusesInput } from './BonusesInput';
import { useCommissions } from '../../../../hooks/useCommissions';

type TCreateBonusInput = {
  isChecked: boolean
};

const CreateBonusInput: FC<TCreateBonusInput> = ({ isChecked }) => {
  const theme = useTheme() as TCustomTheme;
  const { t } = useTranslation('commissions');
  const { isMobile } = useCustomTheme(theme);
  const getCurrency = useAppSelector(getCurrencyByCode);
  const { bonusesCommissionOptions } = useCommissions();
  const selectedCommissionPlan = useAppSelector(getSelectedCommissionPlan);
  const [readMore, setReadMore] = useState(false);

  const dispatch = useAppDispatch();

  const handleBonusStepsChange = (updatedSteps: TCommissionPlan['bonuses']) => {
    dispatch(commissionsActions.setSelectedCommissionsPlan({
      ...selectedCommissionPlan,
      bonuses: updatedSteps,
    }));
  };

  const onBonusRadioChange = (event: ChangeEvent<HTMLInputElement>) => {
    dispatch(commissionsActions.setSelectedCommissionsPlan({
      ...selectedCommissionPlan,
      bonusBase: event.target?.value,
    }));
  };

  const handleChange = () => {
    setReadMore((prev) => !prev);
  };

  return (
    <Grid
      {...(isMobile && {
        paddingX: 0,
      })}
      paddingTop={0}
      size={{ sm: 12, md: 12, lg: 12 }}
    >
      <Box pb={1}>
        <BaseLabel letterSpacing="-0.5px">{t('bonuses')}</BaseLabel>
      </Box>
      <RadioGroup
        row
        value={selectedCommissionPlan.bonusBase}
        onChange={onBonusRadioChange}
      >
        {bonusesCommissionOptions.map((radio) => (
          <FormControlLabel key={radio.id} value={radio.value} control={<Radio disabled={!isChecked} />} label={radio.name} />
        ))}
      </RadioGroup>
      <Divider sx={{
        my: 2,
        borderStyle: 'dashed',
      }}
      />
      <Stack spacing={1}>
        <Typography fontWeight={600} variant="caption">
          {selectedCommissionPlan.bonusBase === 'Locations' ? t('sinceManagersUsually') : t('thisIsAnotherWay')}
        </Typography>
        <Stack>
          <Box pb={1} pt={1}>
            <Button endIcon={<Iconify icon="mdi:help-outline" />} onClick={handleChange} variant="outlined">{t('example')}</Button>
          </Box>
          <Collapse orientation="vertical" in={readMore}>
            <Divider sx={{
              my: 2,
              borderStyle: 'dashed',
            }}
            />
            <Typography fontWeight={600} variant="caption">
              {t('bestSuitedForScenariosLike')}
            </Typography>
            {selectedCommissionPlan.bonusBase !== 'Locations' ? (
              <>
                <br />
                <Typography fontWeight={600} color="primary" variant="caption">
                  {t('dannySoldFor')}
                </Typography>
              </>
            ) : (
              <Stack spacing={1}>
                <Typography fontWeight={600} color="primary" variant="caption">
                  {t('sarahManagesTwoLocations')}
                </Typography>
                <Typography fontWeight={600} variant="caption">
                  {t('example')}
                  :
                </Typography>
                <Typography variant="caption">
                  {t('ifTheTeamInLocationL1Sold30')}
                </Typography>
                <Typography variant="caption">
                  {t('ifTheTeamInLocationL1Sold60')}
                </Typography>
                <Typography fontWeight={600} color="primary" variant="caption">
                  {t('sarahManagesOneLocation')}
                </Typography>
              </Stack>
            )}
          </Collapse>
        </Stack>
      </Stack>
      <Divider sx={{
        my: 2,
        borderStyle: 'dashed',
      }}
      />
      <Box pt={2}>
        <BonusesInput
          bonuses={selectedCommissionPlan?.bonuses}
          onChange={handleBonusStepsChange}
          maxSteps={3}
          isChecked={isChecked}
          currencyMark={getCurrency().mark}
        />
      </Box>
    </Grid>
  );
};

export { CreateBonusInput };
